import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/sonner";

const AuthCallback = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the URL fragment which contains the auth tokens
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const errorDescription = hashParams.get('error_description');
        
        if (errorDescription) {
          throw new Error(errorDescription);
        }
        
        if (!accessToken || !refreshToken) {
          throw new Error('Authentication tokens not found');
        }
        
        // Store tokens
        localStorage.setItem('sb-access-token', accessToken);
        localStorage.setItem('sb-refresh-token', refreshToken);
        
        // Get user session from the backend
        const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';
        const response = await fetch(`${API_URL}/auth/session`, {
          credentials: 'include',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'X-Refresh-Token': refreshToken
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          // Check if the error indicates the user doesn't exist
          if (errorData.message?.toLowerCase().includes('user not found')) {
            toast.error("Account not found. Please sign up first.");
            // Preserve subscription parameter when redirecting to signup
            const selectedSubscription = localStorage.getItem('selected-subscription');
            const signupPath = selectedSubscription ? `/signup?subscription=${selectedSubscription}` : '/signup';
            navigate(signupPath);
            return;
          }
          throw new Error('Failed to get user session');
        }
        
        const data = await response.json();
        
        if (data.success && data.data) {
          // Check if this is a Google sign-up flow
          const isGoogleSignup = localStorage.getItem('is-google-signup') === 'true';
          localStorage.removeItem('is-google-signup'); // Clean up the flag
          
          if (isGoogleSignup) {
            // Redirect to complete sign-up page
            navigate('/auth/google-signup-complete');
          } else if(data.data.email==""){
            toast.error("Account not found. Please sign up first.");
            // Preserve subscription parameter when redirecting to signup
            const selectedSubscription = localStorage.getItem('selected-subscription');
            const signupPath = selectedSubscription ? `/signup?subscription=${selectedSubscription}` : '/signup';
            navigate(signupPath);
            return;
          } else {
            toast.success("Successfully signed in with Google!");
            
            // Check for subscription upgrade after successful Google sign-in
            const selectedSubscription = localStorage.getItem('selected-subscription');
            console.log('AuthCallback - subscription check:', {
              selectedSubscription,
              hasAuthRedirectPath: !!localStorage.getItem('auth-redirect-path'),
              authRedirectPath: localStorage.getItem('auth-redirect-path')
            });
            
            if (selectedSubscription) {
              // Redirect to billing with subscription parameter for upgrade
              const redirectPath = `/billing?subscription=${selectedSubscription}`;
              localStorage.removeItem('auth-redirect-path');
              console.log('AuthCallback - redirecting with subscription:', redirectPath);
              window.location.href = redirectPath;
            } else {
              // Redirect to stored path or dashboard
              const redirectPath = localStorage.getItem('auth-redirect-path') || '/';
              localStorage.removeItem('auth-redirect-path');
              
              console.log('AuthCallback - checking stored redirect path:', {
                redirectPath,
                hasSubscriptionParam: redirectPath.includes('subscription='),
                currentSubscription: localStorage.getItem('selected-subscription')
              });
              
              // Check if the stored redirect path already has a subscription parameter
              if (redirectPath.includes('subscription=')) {
                // The stored path already has subscription parameter, use it as is
                console.log('AuthCallback - using stored path with subscription:', redirectPath);
                window.location.href = redirectPath;
              } else {
                // Check if we have a subscription in localStorage that wasn't in the stored path
                const currentSubscription = localStorage.getItem('selected-subscription');
                if (currentSubscription) {
                  // Add subscription parameter to the redirect path
                  const separator = redirectPath.includes('?') ? '&' : '?';
                  const finalRedirectPath = `${redirectPath}${separator}subscription=${currentSubscription}`;
                  console.log('AuthCallback - adding subscription to redirect path:', finalRedirectPath);
                  window.location.href = finalRedirectPath;
                } else {
                  // No subscription, use the stored path as is
                  console.log('AuthCallback - using stored path without subscription:', redirectPath);
                  window.location.href = redirectPath;
                }
              }
            }
          }
        } else {
          throw new Error(data.message || "Failed to get user information");
        }
        
      } catch (error: unknown) {
        console.error("Auth callback error:", error);
        const errorMessage = error instanceof Error ? error.message : "Authentication failed";
        toast.error(errorMessage);
        navigate('/signin');
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback; 